import { from, Observable, of, pipe, UnaryFunction } from 'rxjs';
import { catchError, concatMap, finalize, pluck, shareReplay, tap } from 'rxjs/operators';

import { MatrixProcessError, MatrixProcessMessage } from '@/models/matrix-error-response';
import { MatrixProcess } from '@/models/matrix-process';
import { MatrixProcessMessageType } from '@/models/matrix-process-message-type-enum';
import { MatrixStatusType } from '@/models/matrix-status-enum';
import {
  addErrorToReport,
  clearReports,
  matrix,
  setCurrentProcess,
  setStatus,
  toggleLoading
} from '@/modules/matrix/observable';
import { controlMatrix, insertMatrix } from '@/resources/http/matrix-upload';
import {
  getControlProcessErrorMessage,
  getIntegrationProcessErrorMessage,
  getMatrixProcessStatusMessage
} from '@/services/matrix-process-messages';

type MatrixProcessStream = Observable<MatrixProcessMessage>;
type MatrixProcessorFunction = (matrixFile: File, index: number) => MatrixProcessStream;
type MatrixProcessErrorFunction = (error: MatrixProcessError) => string;

function matrixProcessNext(
  index: number,
  getProcessErrorMessage: MatrixProcessErrorFunction
): (value: MatrixProcessMessage) => void {
  return processMessage => {
    if (!processMessage.success) {
      const message = getProcessErrorMessage(processMessage);
      return setStatus(index, { type: MatrixStatusType.error, message, isCritical: true });
    }
    if (processMessage.type === MatrixProcessMessageType.CONTROL) {
      return addErrorToReport(index, processMessage.message);
    }
    const isStillInProcessingState = matrix.elements[index].status.type === MatrixStatusType.processing;
    if (processMessage.type === MatrixProcessMessageType.COMPLETE && isStillInProcessingState) {
      const { report } = matrix.elements[index];
      let type: MatrixStatusType = MatrixStatusType.complete;
      if (report.length > 0) {
        const hasRejection = report.some(line => line.rejection);
        type = hasRejection ? MatrixStatusType.completeWithRejection : MatrixStatusType.completeWithAlerts;
      }
      return setStatus(index, { type });
    }
  };
}

function matrixProcessComplete(index: number): () => void {
  return () => {
    const { status } = matrix.elements[index];

    let type = status.type;
    let isCritical = status.isCritical;
    if (type == MatrixStatusType.processing) {
      type = MatrixStatusType.uncomplete;
      isCritical = true;
    }

    let message;
    const isIntegrationProcess = matrix.currentProcess === MatrixProcess.INTEGRATION;
    if (isCritical || isIntegrationProcess) {
      message = getMatrixProcessStatusMessage(type, status.message);
    }

    setStatus(index, { type, message, isCritical });
  };
}

function handleMatrixProcessResponse(
  index: number,
  getProcessErrorMessage: MatrixProcessErrorFunction
): UnaryFunction<MatrixProcessStream, MatrixProcessStream> {
  return pipe(
    catchError((error: Error) => {
      return of({ type: MatrixProcessMessageType.UNKNOWN, message: error.message } as MatrixProcessError);
    }),
    tap({
      next: matrixProcessNext(index, getProcessErrorMessage),
      complete: matrixProcessComplete(index)
    })
  );
}

function triggerControlMatrix(matrixFile: File, index: number) {
  return controlMatrix(matrixFile).pipe(handleMatrixProcessResponse(index, getControlProcessErrorMessage));
}

function triggerInsertMatrix(matrixFile: File, index: number) {
  return insertMatrix(matrixFile).pipe(handleMatrixProcessResponse(index, getIntegrationProcessErrorMessage));
}

function processMatrix(processor: MatrixProcessorFunction): Promise<MatrixProcessMessage> {
  if (matrix.elements.length <= 0) {
    return Promise.reject(new Error('Nothing to process'));
  }

  toggleLoading();
  clearReports();

  return from(matrix.elements)
    .pipe(
      pluck('file'),
      concatMap((matrixFile, index) => {
        setStatus(index, { type: MatrixStatusType.processing });
        return processor(matrixFile, index);
      }),
      finalize(toggleLoading),
      shareReplay()
    )
    .toPromise();
}

export async function control(): Promise<MatrixProcessMessage> {
  setCurrentProcess(MatrixProcess.CONTROL);
  return processMatrix(triggerControlMatrix);
}

export async function integrate(): Promise<MatrixProcessMessage> {
  setCurrentProcess(MatrixProcess.INTEGRATION);
  return processMatrix(triggerInsertMatrix);
}
